import { addToast } from "components/ToastContainer/ToastStore";
import { routeConstants } from "constants/routeConstants";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "slice";
import {
    useGetAgencyDataQuery,
    useGetProfileStrengthQuery,
    useUpdateAgencyLogoMutation,
    useUpdateAgencyProfileDataMutation,
} from "slice/user";
import { validations } from "utils/validations";
import { getInputErrors } from "./getInputErrors";

export const useCompanyDetails = () => {
    const [inputs, setInputs] = useState({
        companyName: "",
        websiteUrl: "",
        aboutCompany: "",
    });

    const [inputErrors, setInputErrors] = useState({
        companyName: "",
        websiteUrl: "",
        aboutCompany: "",
    });

    const { id } = useAppSelector((state) => state.user);
    const { refetch } = useGetProfileStrengthQuery(id ?? "", {
        skip: !id,
    });
    const { data: agencyData, isFetching: isFetchingData } =
        useGetAgencyDataQuery(
            {
                user_id: id ?? "",
            },
            {
                refetchOnMountOrArgChange: true,
            }
        );

    const [profileUrl, setProfileUrl] = useState("");

    const [navigateToNextPage, setNavigateToNextPage] = useState(false);
    const navigateTo = useNavigate();

    const [updateProfileData, { isLoading, isSuccess, isError }] =
        useUpdateAgencyProfileDataMutation();

    const [
        uploadLogo,
        { isLoading: isUploadingFile, isError: hasErrorWhileUploadingFile },
    ] = useUpdateAgencyLogoMutation();

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setInputs((prev) => ({
            ...prev,
            [name]: value,
        }));

        setInputErrors((prev) => ({
            ...prev,
            [name]: validations(name, value, true),
        }));
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            const file = e.target.files[0];
            const acceptedFileTypes = ["image/png", "image/jpg", "image/jpeg"];
            if (!acceptedFileTypes.includes(file.type)) {
                addToast({
                    type: "error",
                    text: "Invalid files selected. Only jpg, png or jpeg files are accepted.",
                });

                return;
            } else {
                const formData = new FormData();
                formData.append("agency[company_logo]", e.target.files[0]);
                const base64File = await toBase64(e.target.files[0]);
                setProfileUrl(base64File as unknown as string);
                uploadLogo({
                    user_id: id ?? "",
                    data: formData,
                });
            }
        }
    };
    const toBase64 = (file: File) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
        });

    const handleSubmit = (next: boolean) => {
        const { hasError, errors } = getInputErrors(inputs);

        setInputErrors(errors);

        if (!hasError) {
            setNavigateToNextPage(next);
            updateProfileData({
                user_id: id ?? "",
                data: {
                    agency: {
                        name: inputs.companyName,
                        website: inputs.websiteUrl,
                        details: inputs.aboutCompany,
                        address: agencyData?.address.address,
                        city: agencyData?.address.city,
                        zip: agencyData?.address.zip,
                        state: agencyData?.address.state,
                        country: agencyData?.address.country,
                    },
                },
            }).then(() => {
                refetch();
            });
        }
    };

    useEffect(() => {
        if (isSuccess) {
            addToast({
                type: "success",
                text: "Data saved successfully!",
            });
            if (navigateToNextPage) {
                navigateTo(
                    routeConstants.profile.editProfile.agency.root +
                        routeConstants.profile.editProfile.agency
                            .contactInformation
                );
            }
        }
    }, [isSuccess, navigateTo, navigateToNextPage]);

    useEffect(() => {
        if (isError || hasErrorWhileUploadingFile) {
            addToast({
                type: "error",
                text: "Something went wrong, please try again.",
            });
        }
    }, [isError, hasErrorWhileUploadingFile]);

    useEffect(() => {
        if (agencyData) {
            setInputs({
                companyName: agencyData?.name ?? "",
                aboutCompany: agencyData?.details ?? "",
                websiteUrl: agencyData?.website ?? "",
            });

            setProfileUrl(agencyData.company_logo ?? "");
        }
    }, [agencyData]);

    return {
        inputs,
        inputErrors,
        isFetchingData,
        profileUrl,
        handleInputChange,
        handleSubmit,
        handleFileUpload,
        isLoading,
        navigateToNextPage,
        isUploadingFile,
    };
};
